import {Engine} from "platform/engine/Engine";
import CoreEngine from "platform/engine/CoreEngine";
import {LangCode} from "platform/enum/LangCode";
import {GetAllCountriesRequest} from "platform/protocol/common/GetAllCountriesRequest";
import {HttpReject} from "platform/network/http/Http";
import {GetAllCountriesResponse} from "platform/protocol/common/GetAllCountriesResponse";
import Utils from "platform/util/Utils";
import {XhrUtil} from "core/util/XhrUtil";
import {ServerType} from "platform/enum/ServerType";
import {TSMap} from "typescript-map";
import {CountryInfo} from "platform/protocol/common/CountryInfo";
import Platform from "platform/Platform";
import {DoGetURLPayload, SetConfiguredCountries} from "core/redux/app/AppReduxActions";
import {SetAuthStatusPayload} from "core/redux/auth/AuthReduxActions";
import {StoreState} from "core/redux/StoreState";
import {LanguageUtil} from "platform/util/LanguageUtil";
import {GetURLByTypeRequest} from "platform/protocol/common/GetURLByTypeRequest";
import {GetUrlType} from "platform/protocol/enum/GetUrlType";
import {UrlType} from "platform/enum/UrlType";
import {GetURLByTypeResponse} from "platform/protocol/common/GetURLByTypeReponse";
import {SetAppReadyPayload, SetUrls} from "platform/redux/core/CoreActions";
import WebUtil from "platform/util/WebUtil";
import {EventType} from "platform/enum/EventType";
import {AutoChartistMessageType} from "core/autochartist/AutoChartistMessageType";
import {AutoChartistTradeNow} from "core/autochartist/AutoChartistTradeNow";
import {TradeSymbol} from "platform/protocol/trading/symbol/TradeSymbol";
import {GoingOpenDeal} from "core/redux/deal/DealReduxActions";
import {PositionDirection} from "platform/protocol/enum/PositionDirection";
import {ShowPopup} from "platform/redux/popups/PopupsActions";
import {PopupActionType, PopupIconType, PopupType} from "platform/redux/popups/PopupsReduxState";
import {TranslationKey} from "enum/TranslationKey";
import Parameter from "platform/util/Parameter";
import {TranslationParam} from "enum/TranslationParam";
import {DpkType} from "enum/DpkType";
import {Dpk} from "platform/dpk/Dpk";
import {DpkParam} from "enum/DpkParam";
import {ExecuteDpk} from "platform/redux/dpk/DpkActions";
import {DepositButtonActionType} from "protocol/sso/DepositButtonActionType";
import {AccountState} from "core/state/AccountState";
import {ServiceType} from "enum/ServiceType";
import {Locale} from "core/format/Locale";

let DpkUrl: string = WebUtil.urlParam("dpk");
const DpkMethod: string = WebUtil.urlParam(DpkParam.Method);
const DpkCurrency: string = WebUtil.urlParam(DpkParam.Currency);
const DpkAmount: string = WebUtil.urlParam(DpkParam.Amount);
const DpkRic: string = WebUtil.urlParam(DpkParam.Ric);
const DpkDirection: string = WebUtil.urlParam(DpkParam.direction);

export default class AppEngine extends Engine {

    private static _instance: AppEngine;

    public static instance(): AppEngine {
        return this._instance || (this._instance = new this());
    }

    public async setup(): Promise<void> {
        await super.setup();
        await CoreEngine.instance().setup();
        await Locale.load();
        if (window && window.addEventListener) {
            window.addEventListener(EventType.Message, (e) => {
                try {
                    const message = JSON.parse(e.data);
                    if (message.type === AutoChartistMessageType.TradeNow) {
                        const tradeNow: AutoChartistTradeNow = message;
                        const symbolName: string = tradeNow?.value?.item?.symbol_name;
                        const direction: number = tradeNow?.value?.item?.direction;
                        let foundSymbol: boolean;
                        if (symbolName) {
                            const {symbols} = Platform.reduxState<StoreState>().symbols;
                            for (let i = 0; i < symbols.size(); i++) {
                                const symbol: TradeSymbol = symbols.values()[i];
                                if (symbol?.text === symbolName) {
                                    foundSymbol = true;
                                    Platform.dispatch(GoingOpenDeal({
                                        SymbolId: symbol.SymbolId,
                                        Direction: direction === 1 ? PositionDirection.Buy : PositionDirection.Sell,
                                        stopLoss: tradeNow?.value?.item?.signal_levels?.stop_loss,
                                        takeProfit: tradeNow?.value?.item?.signal_levels?.target_level,
                                        comments: "Opened by Trading Opportunities"
                                    }));
                                    break;
                                }
                            }
                        }
                        if (!foundSymbol) {
                            Platform.dispatch(ShowPopup({
                                popup: {
                                    type: PopupType.ERROR,
                                    message: {
                                        trKey: TranslationKey.symbolNotFound,
                                        params: [Parameter.Of(TranslationParam.name, symbolName)]
                                    },
                                    showClose: true,
                                    icon: PopupIconType.ERROR,
                                    actions: [{type: PopupActionType.OK}]
                                }
                            }));
                        }
                    }
                } catch (e) {}
            });
        }
    }

    public onAppReady = ({ready}: SetAppReadyPayload): void => {
    }

    public doSetAuthStatus = async (payload: SetAuthStatusPayload) => {
        const {connected, authenticated} = Platform.reduxState<StoreState>().auth;
        if (connected) {
            if (!authenticated && payload.authStatus.authenticated) {
                const langCode: LangCode = LanguageUtil.languageCode();
                this._logger.debug("Fetch countries for lang code: " + langCode);
                const request: GetAllCountriesRequest = {LanguageCode: langCode};
                const answer: [HttpReject, GetAllCountriesResponse] = await Utils.to(this.sendToConfig(request, "GetAllCountries"));
                if (answer[0]) {
                    this._logger.debug("Failed fetch countries list. Status: " + answer[0].status);
                } else {
                    const response: GetAllCountriesResponse = answer[1];
                    if (Utils.isArrayNotEmpty(response.Countries)) {
                        this._logger.debug("Received countries list. Size: " + response.Countries.length);
                        const countries: TSMap<number, CountryInfo> = new TSMap();
                        const namePerCountry: TSMap<string, CountryInfo> = new TSMap();
                        response.Countries.sort((ci1: CountryInfo, ci2: CountryInfo) => Utils.compareString(ci1.LocalizedName, ci2.LocalizedName)).forEach((countryInfo: CountryInfo) => {
                            countryInfo.LocalizedName = (countryInfo.LocalizedName || "").trim();
                            countries.set(countryInfo.Id, countryInfo);
                            namePerCountry.set(countryInfo.LocalizedName, countryInfo);
                        });
                        Platform.dispatch(SetConfiguredCountries({
                            countries, namePerCountry
                        }));
                    } else {
                        this._logger.debug("Countries list empty");
                    }
                }
            }
        }
    }

    public doGetUrl = async ({urlType, parameters}: DoGetURLPayload) => {
        const URLType: GetUrlType = UrlType.getUrlType(urlType);
        if (Utils.isNotNull(URLType)) {
            const langCode: LangCode = LanguageUtil.languageCode();
            const request: GetURLByTypeRequest = {LanguageCode: langCode, URLType};
            const answer: [HttpReject, GetURLByTypeResponse] = await Utils.to(this.sendToConfig(request, "GetURLByType"));
            if (answer[0]) {
                this._logger.debug("Failed fetch url by type " + urlType + ". Status: " + answer[0].status);
            } else {
                const response: GetURLByTypeResponse = answer[1];
                this._logger.debug("Received url for type " + urlType + ". URL: " + response.URL);
                const GetParams: { [key: string]: string } = {
                    "token": response.Token,
                    "language": langCode
                };
                let url: string = response.URL;
                if (urlType === UrlType.Deposit) {
                    url = url.replace("&isCalledFromCompliance=True", "");
                    url = WebUtil.addGetParam(url, "platform", "profit");
                    const dpk: string = Parameter.value("dpk", parameters);
                    if (dpk) {
                        url = WebUtil.addGetParam(url, "dpk", dpk);
                    }
                } else {
                    url = WebUtil.addGetParams(url, GetParams);
                    if (urlType === UrlType.Kyc || urlType === UrlType.Professional || urlType === UrlType.Documents || urlType === UrlType.DepositLimits) {
                        url = WebUtil.addGetParam(url, "platform", "new");
                    }
                }
                Platform.dispatch(SetUrls({urls: [{type: urlType, url}]}));
            }
        }
    }

    public tryExecuteDpkUrl = (): void => {
        const accountState: AccountState = Platform.state(ServiceType.Account);
        const depositBtnType: DepositButtonActionType = accountState.getDepositButtonType();
        const {symbolsLoaded} = Platform.reduxState<StoreState>().symbols;
        if (symbolsLoaded && Utils.isNotNull(depositBtnType)) {
            this._logger.debug("URL dpk: " + DpkUrl + " RIC :" + DpkRic + " direction: " + DpkDirection);
            const dpkType: DpkType = DpkType.deserialize(DpkUrl);
            if (dpkType) {
                const params: Parameter[] = [];
                if (dpkType === DpkType.OpenDeposit || dpkType === DpkType.Deposit) {
                    params.push(Parameter.Of("dpk", Dpk.build(dpkType,
                        Parameter.Of(DpkParam.Method, DpkMethod, false),
                        Parameter.Of(DpkParam.Currency, DpkCurrency, false),
                        Parameter.Of(DpkParam.Amount, DpkAmount, false)
                    )));
                } else if (dpkType === DpkType.openposition) {
                    params.push(Parameter.Of(DpkParam.Ric, DpkRic));
                    params.push(Parameter.Of(DpkParam.direction, DpkDirection));
                }
                Platform.dispatch(ExecuteDpk({
                    dpk: DpkUrl,
                    params
                }));
            }
            DpkUrl = null;
        }
    }

    private sendToConfig(request: any, path: string): Promise<any> {
        return XhrUtil.sendAuthenticatedTo(ServerType.Compliance, request, "ComplianceWebSite/ComplianceConfigurationsService.svc/json/" + path);
    }
}
